/* eslint-disable @typescript-eslint/ban-ts-comment */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import appConfig from '../helpers/appConfig';
import { nvFetch } from '../helpers/nvFetch';
import { isElectron } from '../helpers/platform';
import * as platform from '../helpers/platformApi';
import nvStorage from '../helpers/storage';
import { callHistoryReset } from './callHistory';
import { fetchExtensionDetails } from './extension';
import { finaliseLogout } from './user';
const initialState = {
    volume: 1,
    selectedAudioInputDeviceId: 'default',
    selectedAudioOutputDeviceId: 'default',
    selectedRingingOutputDeviceId: 'default',
    darkMode: false,
    activeCustomer: undefined,
    customers: [],
    isFetchingCustomers: false,
    isFetchingLinkedUsers: false,
    linkedUsersList: [],
    selectedCallerId: undefined,
    hasGrantedAudio: false,
    lastOpenPreferencesRequest: undefined,
    error: undefined,
};
export const fetchLinkedUsers = createAsyncThunk('client/fetchLinkedUsers', (_, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    const { client: { isFetchingLinkedUsers, activeCustomer }, } = thunkAPI.getState();
    if (isFetchingLinkedUsers || !activeCustomer) {
        return;
    }
    thunkAPI.dispatch(linkedUsersFetching());
    const fetchLinkedUsers = () => __awaiter(void 0, void 0, void 0, function* () {
        const fetchResult = yield nvFetch(`/customers/${activeCustomer.id}/linkedusers`);
        return (yield fetchResult.json()).items;
    });
    const fetchMe = () => __awaiter(void 0, void 0, void 0, function* () {
        const fetchResult = yield nvFetch(`/customers/${activeCustomer.id}/linkedusers/me`);
        return (yield fetchResult.json());
    });
    const [linkedUsersList, linkedUser] = yield Promise.all([
        fetchLinkedUsers(),
        fetchMe(),
    ]);
    thunkAPI.dispatch(linkedUsersFetched({ linkedUsersList, linkedUser }));
    if (linkedUser.defaultExtension) {
        thunkAPI.dispatch(fetchExtensionDetails(linkedUser.defaultExtension));
    }
}));
export const setActiveCustomerAndFetchLinkedUsers = createAsyncThunk('client/setActiveCustomerAndFetchLinkedUsers', (customerId, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    const activeCustomer = thunkAPI
        .getState()
        .client.customers.find((c) => c.id === customerId);
    nvStorage.set('selectedCustomerId', customerId);
    // @ts-expect-error
    thunkAPI.dispatch(setActiveCustomer(activeCustomer));
    thunkAPI.dispatch(callHistoryReset());
    thunkAPI.dispatch(fetchLinkedUsers());
}));
export const fetchCustomers = createAsyncThunk('client/fetchCustomers', (_, ThunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    const { client: { isFetchingCustomers }, } = ThunkAPI.getState();
    if (isFetchingCustomers) {
        return;
    }
    ThunkAPI.dispatch(customersFetching());
    const fetchResult = yield nvFetch(`/customers`);
    const customers = (yield fetchResult.json()).items;
    ThunkAPI.dispatch(customersFetched(customers));
    // console.log('FETCHED', res);
    platform.hasLoggedIn(customers.map((entry) => ({
        name: entry.company,
        id: entry.id,
    })));
    let selectedCustomerId;
    if (customers.length === 1) {
        selectedCustomerId = customers[0].id;
    }
    else if (isElectron() && customers.length > 1) {
        const savedSelectedCustomer = nvStorage.get('selectedCustomerId');
        const customer = customers.find((c) => c.id === savedSelectedCustomer);
        if (customer) {
            selectedCustomerId = customer.id;
        }
    }
    if (selectedCustomerId) {
        platform.hasSelectedTeam(selectedCustomerId);
        ThunkAPI.dispatch(setActiveCustomerAndFetchLinkedUsers(selectedCustomerId));
    }
}));
const clientSlice = createSlice({
    name: 'client',
    initialState,
    reducers: {
        customersFetching: (state) => {
            state.isFetchingCustomers = true;
        },
        customersFetched: (state, action) => {
            state.isFetchingCustomers = false;
            state.error = undefined;
            state.customers = action.payload;
        },
        setActiveCustomer: (state, action) => {
            state.activeCustomer = action.payload;
        },
        linkedUsersFetching: (state) => {
            state.isFetchingLinkedUsers = true;
        },
        linkedUsersFetched: (state, action) => {
            const { linkedUsersList, linkedUser } = action.payload;
            const map = {};
            linkedUsersList.forEach((x) => {
                if (x.defaultExtension && !map[x.defaultExtension]) {
                    map[x.defaultExtension] = x.email;
                }
            });
            state.isFetchingLinkedUsers = false;
            state.error = undefined;
            state.linkedUsersList = linkedUsersList;
            state.linkedUser = linkedUser;
            state.linkedUsersMap = map;
        },
        setVolume: (state, action) => {
            const { extension } = appConfig;
            extension === null || extension === void 0 ? void 0 : extension.setVolume(action.payload);
            state.volume = action.payload;
        },
        selectAudioInputDevice: (state, action) => {
            nvStorage.set('selectedAudioInputDeviceId', action.payload);
            state.selectedAudioInputDeviceId = action.payload;
        },
        selectAudioOutputDevice: (state, action) => {
            nvStorage.set('selectedAudioOutputDeviceId', action.payload);
            state.selectedAudioOutputDeviceId = action.payload;
        },
        selectRingingOutputDevice: (state, action) => {
            nvStorage.set('selectedRingingOutputDeviceId', action.payload);
            state.selectedRingingOutputDeviceId = action.payload;
        },
        enableDarkMode: (state) => {
            state.darkMode = true;
        },
        disableDarkMode: (state) => {
            state.darkMode = false;
        },
        setCallerId: (state, action) => {
            state.selectedCallerId = action.payload;
        },
        grantAudio: (state, action) => {
            state.hasGrantedAudio = action.payload;
        },
        openPreferences: (state) => {
            state.lastOpenPreferencesRequest = Date.now();
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCustomers.rejected, (state, action) => {
            state.isFetchingCustomers = false;
            state.error = action.error;
        })
            .addCase(fetchLinkedUsers.rejected, (state, action) => {
            state.isFetchingLinkedUsers = false;
            state.error = action.error;
        })
            .addCase(finaliseLogout.fulfilled, () => initialState);
    },
});
export const { customersFetching, customersFetched, setActiveCustomer, linkedUsersFetching, linkedUsersFetched, setVolume, selectAudioInputDevice, selectAudioOutputDevice, selectRingingOutputDevice, enableDarkMode, disableDarkMode, setCallerId, grantAudio, openPreferences, } = clientSlice.actions;
export default clientSlice.reducer;
